/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Jan 25 2024
 **/

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { EventID } from "@/frameworks/core/commons";
import { useFileManager } from "@/frameworks/core/hooks";
import { MessageInValidateFilename } from "@/frameworks/core/utils";
import { useToast } from "@/frameworks/designStuff/components";
import { useEventHandler } from "@rfice/eventhandler";

export default function useFileRename(file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number]) {
	const { renameFolder, renameFile, currentFolder } = useFileManager();
	const queryClient = useQueryClient();
	const toast = useToast();

	const [isUpdateStarted, setIsUpdateStarted] = useState(false);
	const [fileName, setFileName] = useState(file.name || file.title);
	const [fileNameForUpdate, setFileNameForUpdate] = useState(file.name || "");

	const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
		setFileNameForUpdate(ev.target.value);
	};

	const onNameUpdated = () => {
		setFileName(fileNameForUpdate);

		queryClient.setQueryData<FileBox.Folders.GetMyFoldersResponse>(["myFolders", currentFolder.id], (prev) => {
			if (!prev) return prev;
			return [...prev].map((v) => {
				if (v.id === file.id) {
					return { ...v, name: fileNameForUpdate };
				}
				return v;
			});
		});

		queryClient.refetchQueries({ queryKey: ["folderFileList", { all: currentFolder.all, id: currentFolder.id }] });
	};

	const updateFileName = async () => {
		if (isUpdateStarted === false || fileNameForUpdate === file.name || fileNameForUpdate.length === 0) {
			setIsUpdateStarted(false);
			return;
		}
		if (MessageInValidateFilename(fileNameForUpdate)) {
			toast.show({
				type: "error",
				message: MessageInValidateFilename(fileNameForUpdate)
			});
			return;
		}

		renameFile({
			id: file.id,
			name: fileNameForUpdate
		})
			.then(() => {
				onNameUpdated();
			})
			.catch((err) => {
				console.error(err);
				toast.show({ type: "error", message: "파일명 변경 실패" });
			})
			.finally(() => {
				setIsUpdateStarted(false);
			});
	};

	const updateFolderName = async () => {
		if (isUpdateStarted === false || fileNameForUpdate === file.name || fileNameForUpdate.length === 0) {
			setIsUpdateStarted(false);
			return;
		}

		renameFolder({
			id: file.id,
			name: fileNameForUpdate,
			referenceId: currentFolder.referenceID || "",
			referenceType: currentFolder.referenceType || "INDIVIDUAL"
		})
			.then(() => {
				onNameUpdated();
			})
			.catch((err) => {
				console.error(err);
				toast.show({ type: "error", message: "폴더명 변경 실패" });
			})
			.finally(() => {
				setIsUpdateStarted(false);
			});
	};

	useEventHandler(
		EventID.UPDATE_FILE_NAME,
		(targetFile: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number]) => {
			if (targetFile.id !== file.id) return;
			setIsUpdateStarted(true);
		},
		[],
		{ disposing: isUpdateStarted }
	);

	return { fileName, fileNameForUpdate, isUpdateStarted, updateFileName, updateFolderName, handleChange };
}
